<script setup lang="ts">
import { Button } from '@forgd/ui-kit/button'
import { apiFetch } from '#core/composables/useApiFetch'
import { isUnderMaintenance, maintenancePathPrefixes } from '#core/utils/maintenance'
import { useIntervalFn } from '@vueuse/core'

// Routes gated by an 'under-maintenance:<route-prefix>' flag render the dashboard layout (nav stays usable) with this panel instead of the page, which never mounts or fetches.
const route = useRoute()
const auth = useAuth()
const isLiquidityPortal = useRuntimeConfig().public.portal === 'liquidity'

const prefixes = computed(() => maintenancePathPrefixes(auth.featureFlags))
const active = computed(() => isUnderMaintenance(route.path, prefixes.value))

const homePath = isLiquidityPortal ? '/dashboard' : '/'
const homeAvailable = computed(() => !isUnderMaintenance(homePath, prefixes.value))

// Crawlers and uptime monitors must not treat a maintenance response as healthy.
if (import.meta.server && active.value) {
  const event = useRequestEvent()
  if (event) {
    setResponseStatus(event, 503, 'Service Unavailable')
  }
}
useHead(() => (active.value ? { meta: [{ name: 'robots', content: 'noindex' }] } : {}))

// Sweep in already-open tabs by refreshing flags every minute; a failed poll keeps the last known flags rather than silently lifting the wall.
if (import.meta.client) {
  useIntervalFn(async () => {
    // Site-wide env-var wall: reload so the server middleware serves the static maintenance page.
    try {
      const { underMaintenance } = await $fetch<{ underMaintenance: boolean }>('/__under-maintenance')
      if (underMaintenance) {
        window.location.reload()
        return
      }
    }
    catch {
      // status route unreachable — leave the tab alone
    }
    try {
      auth.setFeatureFlags(await apiFetch('/feature-flags', {}))
    }
    catch {
      // keep last known flags
    }
  }, 60_000)
}
</script>

<template>
  <NuxtLayout v-if="active" name="dashboard">
    <div class="grow min-h-[60vh] flex flex-col gap-8 items-center justify-center px-6 text-center">
      <CoreLogo :dark="true" />

      <div class="space-y-2">
        <h1 class="text-4xl text-primary-900">
          This section is under maintenance
        </h1>
        <p class="text-lg text-body">
          We&rsquo;re making improvements to this part of Forgd and it&rsquo;s temporarily unavailable.
        </p>
        <p class="text-lg text-body">
          We&rsquo;ll be back shortly &mdash; thanks for your patience.
        </p>
      </div>

      <Button v-if="homeAvailable" size="xl" @click="navigateTo(homePath)">
        Back to Home
      </Button>
    </div>
  </NuxtLayout>
  <slot v-else />
</template>
